import { useEffect, useMemo, useState } from "react";
import * as React from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";

import { useDebounce } from "hooks";
import { truncate } from "lodash";

import { DEFAULT_LABEL_CONFIG } from "app/themes/components/input";
import { QUICKBOOKS_PRODUCT_TYPES } from "constants/quickbooks";
import {
  QUICKBOOKS_DEFAULT_LIMIT,
  useGetQuickBooksProductsQuery
} from "services/quickbooks";
import { QuickBookProduct } from "services/types/quickbooks.type";

type QuickBooksProductSelectProps = {
  placeholder?: string;
  required?: boolean;
  label?: string;
  quickBooksCompanyId: number;
  value: Partial<QuickBookProduct> | null;
  disablePortal?: boolean;
  openOnFocus?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  onOptionChanged: (value: Partial<QuickBookProduct> | null) => void;
};

export default function QuickBooksProductSelect({
  placeholder = "Search QuickBooks Products by name",
  required,
  label,
  quickBooksCompanyId,
  value,
  disabled,
  disablePortal = false,
  openOnFocus = false,
  onOptionChanged,
  sx
}: QuickBooksProductSelectProps) {
  const [search, setSearch] = useState<string>("");
  const { value: dSearch, reset: resetDebouncedSearch } = useDebounce(search);

  useEffect(() => {
    const name = value?.quickbooks_product_name ?? "";
    setSearch(name);
    resetDebouncedSearch(name);
  }, [resetDebouncedSearch, value?.quickbooks_product_name]);

  const [open, setOpen] = useState<boolean>(false);
  const [hideDeleted, setHideDeleted] = useState<boolean>(true);

  const {
    data: quickBooksProducts = [],
    isUninitialized: isQuickBooksUninitialized,
    isLoading: isLoadingQuickBooksProducts,
    isFetching: isFetchingQuickBooksProducts
  } = useGetQuickBooksProductsQuery(
    {
      limit: QUICKBOOKS_DEFAULT_LIMIT,
      search: dSearch,
      quickbooks_company: quickBooksCompanyId
    },
    {
      skip: !quickBooksCompanyId,
      refetchOnMountOrArgChange: true
    }
  );

  const isSearching = [
    isQuickBooksUninitialized,
    isLoadingQuickBooksProducts,
    isFetchingQuickBooksProducts
  ].some(Boolean);

  const renderInput = React.useCallback(
    (params: any) => {
      return (
        <TextField
          {...params}
          placeholder={placeholder}
          onClick={() => !open && setOpen(true)}
          InputLabelProps={{ disabled: false }}
          autoFocus
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              onOptionChanged(value);
            }
          }}
        />
      );
    },

    [onOptionChanged, open, value, placeholder]
  );

  const filteredProducts = useMemo(() => {
    return hideDeleted
      ? quickBooksProducts.filter((p) => p.is_active)
      : quickBooksProducts;
  }, [hideDeleted, quickBooksProducts]);

  const options = useMemo(() => {
    return isSearching ? [] : [{ id: undefined }, ...filteredProducts];
  }, [isSearching, filteredProducts]);

  return (
    <FormControl
      sx={{
        width: "100%",
        ...sx
      }}
    >
      <Stack direction="row" alignItems="center" mb={0.5}>
        {Boolean(label) && (
          <FormLabel required={required} sx={DEFAULT_LABEL_CONFIG}>
            {label}
          </FormLabel>
        )}

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideDeleted}
              onChange={(e) => setHideDeleted(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Hide deleted</Typography>}
          labelPlacement="start"
          sx={{ mr: 0, ml: "auto", my: label ? -1 : 0 }}
        />
      </Stack>

      <Autocomplete
        clearIcon={
          <Box
            onClick={(event) => {
              event.stopPropagation();
              setSearch("");
              onOptionChanged(null);
            }}
          >
            <ClearIcon fontSize="small" />
          </Box>
        }
        disabled={disabled}
        disablePortal={disablePortal}
        openOnFocus={openOnFocus}
        loading={isSearching}
        fullWidth
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={search}
        value={value}
        open={open}
        options={options}
        getOptionLabel={(option) => {
          return option?.quickbooks_product_name ?? "None";
        }}
        renderOption={(props: object, option: Partial<QuickBookProduct>) => (
          <ListItem {...props}>
            {option?.id ? (
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 700, my: 0.25 }}>
                  {option?.quickbooks_product_name}
                </Typography>

                {Boolean(option?.quickbooks_product_description) && (
                  <Typography sx={{ color: grey[700], mb: 0.25 }}>
                    {truncate(option?.quickbooks_product_description || "", {
                      length: 64,
                      omission: "…"
                    })}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 0.25 }}>
                  {QUICKBOOKS_PRODUCT_TYPES.getFromCode(
                    option?.quickbooks_product_type
                  )}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2">None</Typography>
            )}
          </ListItem>
        )}
        isOptionEqualToValue={(
          option: Partial<QuickBookProduct>,
          value: Partial<QuickBookProduct>
        ) => {
          return option?.id === value?.id;
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (Boolean(event)) {
            if (reason === "input") {
              setSearch(newInputValue);
            } else {
              setSearch("");
            }
          }
        }}
        popupIcon={<ArrowDropDownIcon />}
        renderInput={renderInput}
        onChange={(event, data) => {
          event.stopPropagation();
          if (data?.id) {
            setOpen(false);
          }
          requestAnimationFrame(() => {
            onOptionChanged?.(data?.id ? data : null);
          });
        }}
      />
    </FormControl>
  );
}
